<template>
  <div 
    ref="container" 
    :class="className" 
    class="datepicker">
    <div class="btn-group summary-group">
      <button 
        v-show="shouldDisplayNavigationButtons"
        class="btn white prev" 
        @click="previousRange()"/>
      <div
        :class="{'rounded-btn': !shouldDisplayNavigationButtons}"
        class="summary">
        <button 
          :class="{'popup-open': showPopup, 'popup-close': !showPopup}" 
          class="btn white" 
          @click="popup()">{{ rangeText }}</button>
      </div>
      <button
        v-show="shouldDisplayNavigationButtons"
        :disabled="!canGoNextRange"
        class="btn white next"
        @click="nextRange()"/>
    </div>
    <div 
      v-if="showPopup" 
      class="datepicker-popup">
      <div
        v-show="showDatePicker"
        class="datepicker-inner">
        <div class="datepicker-body">
          <div 
            v-if="!(showTimePicker || showMinuteInput || showHourInput || showSecondInput)"
            class="datepicker-dateInput">
            <div class="datepicker-ctrl">
              <span 
                v-show="canGoPrevious" 
                class="datepicker-preBtn glyphicon glyphicon-chevron-left"
                aria-hidden="true" 
                @click="previous()"/>
              <span 
                v-show="canGoNext" 
                class="datepicker-nextBtn glyphicon glyphicon-chevron-right"
                aria-hidden="true" 
                @click="next()"/>
              <p @click="selectMonth(currentDate)">{{ currentDate.format('MMMM YYYY') }}</p>
            </div>
            <div class="datepicker-weekRange">
              <span 
                v-for="(w, index) in daysOfWeek" 
                :key="index">{{ w }}</span>
            </div>
            <div class="datepicker-dateRange">
              <span 
                v-for="(d, index) in dateRange" 
                :key="index" 
                :class="d.sclass" 
                @click="selectDate(d.date)">{{ d.text }}</span>
            </div>
          </div>
          <div
            v-if="showDateRangeInfo" 
            class="date-range-info">
            <p>DATE RANGE</p>
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 5px;">
              <input 
                v-model="startDateDisplay" 
                type="text" 
                class="form-control date-input" 
                placeholder="DD-MM-YYYY"
                @input="onDateInputChange"
              >
              <span style="flex-shrink: 0;">-</span>
              <input 
                v-model="endDateDisplay" 
                type="text" 
                class="form-control date-input" 
                placeholder="DD-MM-YYYY"
                @input="onDateInputChange"
              >
            </div>
            <div 
              v-if="dateInputError" 
              class="text-danger" 
              style="font-size: 12px; margin-bottom: 5px;">
              {{ dateInputError }}
            </div>
          </div>
          <div
            v-if="showTime && !(showTimePicker || showMinuteInput || showHourInput || showSecondInput)"
            :class="['datepicker-timeInput', { 'has-date-range-info': showDateRangeInfo }]">
            <p>Start</p>
            <div>
              <span
                class="clickable"
                @click="openTimePicker('start')">{{ startHour }}</span>
              <span class="colon">:</span>
              <span
                class="clickable"
                @click="openTimePicker('start')">{{ startMinute }}</span>
              <span class="colon">:</span>
              <span
                class="clickable"
                @click="openTimePicker('start')">{{ startSecond }}</span>
            </div>
            <p>End</p>
            <div>
              <span
                class="clickable"
                @click="openTimePicker('end')">{{ endHour }}</span>
              <span class="colon">:</span>
              <span
                class="clickable"
                @click="openTimePicker('end')">{{ endMinute }}</span>
              <span class="colon">:</span>
              <span
                class="clickable"
                @click="openTimePicker('end')">{{ endSecond }}</span>
            </div>
          </div>
          <div 
            v-if="showTimePicker" 
            class="timepicker">
            <div class="timepicker-table">
              <div class="timepicker-row">
                <div>
                  <span 
                    class="btn btn-icon btn-sm glyphicon glyphicon-chevron-up" 
                    @click="increaseHour()"/>
                </div>
                <div/>
                <div>
                  <span 
                    class="btn btn-icon btn-sm glyphicon glyphicon-chevron-up" 
                    @click="increaseMinute()"/>
                </div>
                <div/>
                <div>
                  <span
                    class="btn btn-icon btn-sm glyphicon glyphicon-chevron-up"
                    @click="increaseSecond()"/>
                </div>
                <div/>
              </div>
              <div class="timepicker-row">
                <div class="timepicker-fc">
                  <span 
                    class="btn btn-sm" 
                    @click="openHourInput()">{{ renderTimeNumber(selectedHour, 2, '0') }}</span>
                </div>
                <div>:</div>
                <div class="timepicker-fc">
                  <span 
                    class="btn btn-sm" 
                    @click="openMinuteInput()">{{ renderTimeNumber(selectedMinute, 2, '0') }}</span>
                </div>
                <div>:</div>
                <div class="timepicker-fc">
                  <span
                    class="btn btn-sm"
                    @click="openSecondInput()">{{ renderTimeNumber(selectedSecond, 2, '0') }}</span>
                </div>
              </div>
              <div class="timepicker-row">
                <div>
                  <span 
                    class="btn btn-icon btn-sm glyphicon glyphicon-chevron-down" 
                    @click="decreaseHour()"/>
                </div>
                <div/>
                <div>
                  <span 
                    class="btn btn-icon btn-sm glyphicon glyphicon-chevron-down" 
                    @click="decreaseMinute()"/>
                </div>
                <div/>
                <div>
                  <span
                    class="btn btn-icon btn-sm glyphicon glyphicon-chevron-down"
                    @click="decreaseSecond()"/>
                </div>
                <div/>
              </div>
            </div>
            <div class="timepicker-buttons">
              <button 
                v-if="showApplyTimeButton" 
                type="button" 
                class="btn btn-block btn-sm white" 
                @click="applyTime()">Apply</button>
              <div 
                v-if="showHourInput" 
                class="datepicker-hourInput">
                <span 
                  v-for="(h, index) in hoursOfDay" 
                  :key="index" 
                  @click="selectHour(h)">{{ h }}</span>
              </div>
              <div 
                v-if="showMinuteInput" 
                class="datepicker-minuteInput">
                <span 
                  v-for="(m, index) in minutesOfHour" 
                  :key="index" 
                  @click="selectMinute(m)">{{ m }}</span>
              </div>
              <div
                v-if="showSecondInput"
                class="datepicker-minuteInput">
                <span
                  v-for="(s, index) in minutesOfHour"
                  :key="index"
                  @click="selectSecond(s)">{{ s }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="datepicker-shortcuts">
          <div 
            v-show="!showShift" 
            class="btn-group-vertical">
            <button 
              type="button" 
              class="btn btn-fw btn-sm white" 
              @click="selectToday()">Today</button>
            <button 
              type="button" 
              class="btn btn-fw btn-sm white" 
              @click="selectYesterday()">Yesterday</button>
            <button 
              v-show="showDateRange"
              type="button" 
              class="btn btn-fw btn-sm white" 
              @click="selectThisWeek()">This Week</button>
            <button
              v-show="showDateRange"
              type="button" 
              class="btn btn-fw btn-sm white" 
              @click="selectLastWeek()">Last Week</button>
            <button
              v-show="showDateRange"
              type="button" 
              class="btn btn-fw btn-sm white"
              @click="selectThisMonth()">This Month</button>
            <button
              v-show="showDateRange" 
              type="button" 
              class="btn btn-fw btn-sm white"
              @click="selectLastMonth()">Last Month</button>
            <button 
              v-if="allowEmpty" 
              type="button" 
              class="btn btn-fw btn-sm" 
              @click="clearDate()">Clear</button>
          </div>
          <div 
            v-show="showShift" 
            class="shift row-row">
            <div class="row-body">
              <div class="row-inner">
                <shiftpicker 
                  ref="shiftpicker" 
                  :since="startDate" 
                  :until="endDate"/>
              </div>
            </div>
          </div>
          <div
            v-show="false"
            class="shortcut-selector">
            <button 
              class="btn btn-icon btn-sm white" 
              @click="showShift=!showShift">
              <i class="material-icons md-24">&#xe8d3;</i>
            </button>
          </div>
          <div
            class="shortcut-selector">
            <button
              class="btn btn-sm white"
              @click="applyDate()">Apply</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import leftPad from 'left-pad';
import shiftpicker from './Shiftpicker.vue';
import m from 'moment';
import range from 'moment-range';
import twix from 'twix';
import Feature from '@js/plugins/feature';

export default {
    components: {
        shiftpicker,
    },

    props: {
        format: {
            type: String,
            default: 'YYYY-MM-DD',
        },
        min: {
            type: Object,
            default: () => m('2014-01-01'),
        },
        max: {
            type: Object,
            default: () => m().endOf('day'),
        },
        start: {
            type: Object,
            default: null,
        },
        end: {
            type: Object,
            default: null,
        },
        useVuex: {
            type: Boolean,
            default: true,
        },
        className: {
            type: String,
            default: '',
        },
        autoInitDate: {
            type: Boolean,
            default: true,
        },
        allowEmpty: {
            type: Boolean,
            default: false,
        },
        showTime: {
            type: Boolean,
            default: true,
        },
        showDateRange: {
            type: Boolean,
            default: true,
        },
        showDateRangeInfo: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            clientWidth: 0,
            currentDate: m(),
            startDate: m().startOf('day'),
            endDate: m().endOf('day'),
            daysOfWeek: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
            minutesOfHour: [
                '00',
                '05',
                '10',
                '15',
                '20',
                '25',
                '30',
                '35',
                '40',
                '45',
                '50',
                '55',
            ],
            hoursOfDay: [
                '00',
                '01',
                '02',
                '03',
                '04',
                '05',
                '06',
                '07',
                '08',
                '09',
                '10',
                '11',
                '12',
                '13',
                '14',
                '15',
                '16',
                '17',
                '18',
                '19',
                '20',
                '21',
                '22',
                '23',
            ],
            dateRange: [],
            hasStart: true,
            hasEnd: true,
            showPopup: false,
            showMinuteInput: false,
            showSecondInput: false,
            showHourInput: false,
            showShift: false,
            showTimePicker: false,
            showApplyTimeButton: true,
            selectedHour: 0,
            selectedMinute: 0,
            selectedSecond: 0,
            selectedMeridiem: 'AM',
            timeInputMode: 'start',
            showDatePicker: true,
            startDateInput: '',
            endDateInput: '',
            dateInputError: '',
            dateInputTimeout: null,
        };
    },

    computed: {
        canGoPrevious() {
            let newDate = m(this.currentDate).subtract(1, 'month');
            return newDate.isSameOrAfter(
                this.getMinimumDate().startOf('month'),
            );
        },

        canGoNext() {
            let newDate = m(this.currentDate).add(1, 'month');
            return newDate.isSameOrBefore(this.getMaximumDate().endOf('month'));
        },

        canGoNextRange() {
            let start;
            if (this.rangeIsExactlyOneMonth()) {
                start = m(this.getStartDate())
                    .add(1, 'month')
                    .startOf('month');
            } else {
                let range =
                    m
                        .range(this.getStartDate(), this.getEndDate())
                        .diff('days') + 1;
                start = m(this.getStartDate()).add(range, 'day');
            }
            return start.isSameOrBefore(m().endOf('day'));
        },

        startDateText() {
            return this.getStartDate().format('YYYY-MM-DD HH:mm:ss');
        },

        endDateText() {
            return this.getEndDate().format('YYYY-MM-DD HH:mm:ss');
        },

        startHour() {
            return this.getStartDate().format('HH');
        },

        endHour() {
            return this.getEndDate().format('HH');
        },

        startMinute() {
            return this.getStartDate().format('mm');
        },

        endMinute() {
            return this.getEndDate().format('mm');
        },

        startSecond() {
            return this.getStartDate().format('ss');
        },

        endSecond() {
            return this.getEndDate().format('ss');
        },

        startMeridiem() {
            return this.getStartDate().format('A');
        },

        endMeridiem() {
            return this.getEndDate().format('A');
        },

        rangeText() {
            if (this.noDateDefine()) {
                return 'Filter Date';
            } else {
                return m(this.getStartDate())
                    .locale('en-gb')
                    .twix(this.getEndDate(), {
                        allDay: true,
                    })
                    .format();
            }
        },

        activeOutlet() {
            return this.$store.getters.activeOutlet;
        },

        shouldDisplayNavigationButtons() {
            return this.clientWidth >= 48 * 3;
        },

        startDateDisplay: {
            get() {
                if (this.startDateInput !== '') {
                    return this.startDateInput;
                }
                if (this.noDateDefine()) {
                    return '';
                }
                return this.getStartDate().format('DD-MM-YYYY');
            },
            set(value) {
                this.startDateInput = value;
            },
        },

        endDateDisplay: {
            get() {
                if (this.endDateInput !== '') {
                    return this.endDateInput;
                }
                if (this.noDateDefine()) {
                    return '';
                }
                return this.getEndDate().format('DD-MM-YYYY');
            },
            set(value) {
                this.endDateInput = value;
            },
        },
    },

    watch: {
        currentDate() {
            this.generateDateRange();
        },

        startDate() {
            this.generateDateRange();
        },

        endDate() {
            this.generateDateRange();
        },

        activeOutlet() {
            this.openDatePicker();

            this.$store.commit('setReportRange', {
                start: this.startDate,
                end: this.endDate,
            });
        },
    },

    mounted() {
        window.addEventListener('resize', this.handleResize);
        document.addEventListener('click', this.handleClickOutside, true);

        this.handleResize();
        this.setStartAndEndDate();
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        document.removeEventListener('click', this.handleClickOutside, true);

        if (this.dateInputTimeout) {
            clearTimeout(this.dateInputTimeout);
        }
    },

    methods: {
        applyDate() {
            this.broadcastChange();
            this.closePopup();
        },

        popup() {
            if (this.showPopup) {
                this.closePopup();
            } else {
                this.openPopup();
            }
        },

        openPopup() {
            this.showMinuteInput = false;
            this.showHourInput = false;
            this.showTimePicker = false;
            this.showApplyTimeButton = true;
            this.showPopup = true;
            this.generateDateRange();
            this.openDatePicker();
        },

        closePopup(s) {
            this.showPopup = false;
        },

        openDatePicker() {
            this.showDatePicker = true;
        },

        handleClickOutside(e) {
            const el = this.$refs.container;

            if (!el.contains(e.target)) {
                if (this.showPopup) {
                    this.closePopup();
                }
            }
        },

        handleResize(e) {
            this.clientWidth = this.$el.clientWidth;
        },

        generateDateRange() {
            this.dateRange = [];

            let that = this;

            let start = m(this.currentDate)
                .startOf('month')
                .startOf('week');

            let end = m(this.currentDate)
                .endOf('month')
                .endOf('week');

            if (this.extraPreviousWeekEnabled()) {
                start.subtract(1, 'week');
            }

            if (
                !this.extraPreviousWeekEnabled() &&
                this.extraNextWeekEnabled()
            ) {
                end.add(1, 'week');
            }

            let temp = m.range(start, end);

            if (temp.diff('week') < 5) {
                end.add(1, 'week');
            }

            let range = m.range(start, end);

            range.by('days', function(moment) {
                that.dateRange.push({
                    date: moment.startOf('day'),
                    text: moment.format('D'),
                    sclass: that.getDateClass(moment),
                });
            });
        },

        clampDate(date) {
            if (date == null) {
                return;
            }

            if (date.isAfter(this.getMaximumDate())) {
                return this.getMaximumDate();
            }

            if (date.isBefore(this.getMinimumDate())) {
                return this.getMinimumDate();
            }

            return date;
        },

        selectDate(date) {
            if (
                this.getMinimumDate().isAfter(date) ||
                this.getMaximumDate().isBefore(date)
            ) {
                return;
            }

            if (this.hasStart && !this.hasEnd) {
                if (date.isBefore(this.startDate)) {
                    var temp = m(this.startDate);
                    this.selectRange(date, temp);
                    this.hasEnd = true;
                } else {
                    this.selectRange(this.startDate, date);
                    this.hasEnd = true;
                }

                return;
            }

            if (this.hasStart && this.hasEnd) {
                this.selectRange(date, date);
                this.hasEnd = false;
            }
        },

        setStartDate(date, time = true) {
            var finalDate = this.parseDate(date, null);

            if (finalDate != null && time) {
                finalDate = finalDate.startOf('day');
            }

            this.startDate = this.clampDate(finalDate);
        },

        setEndDate(date, time = true) {
            var finalDate = this.parseDate(date, null);

            if (finalDate != null && time) {
                finalDate = finalDate.endOf('day');
            }

            if (finalDate != null && finalDate.isBefore(this.getStartDate())) {
                finalDate = m(this.getStartDate());
            }

            this.endDate = this.clampDate(finalDate);
        },

        parseDate(date, def = m()) {
            if (m.isMoment(date)) {
                return m(date);
            }

            let parsed = m(date, this.format);

            if (parsed.isValid()) {
                return parsed;
            }

            return def;
        },

        navigateToDate(date) {
            if (!date) return;

            let month = date.month();
            let year = date.year();

            this.currentDate = m(this.currentDate)
                .month(month)
                .year(year);
        },

        selectRange(start, end, time = true) {
            this.setStartDate(start, time);
            this.setEndDate(end, time);
            this.navigateToDate(this.clampDate(start));
        },

        clearDate() {
            this.selectRange(null, null);
            this.closePopup();
        },

        selectToday() {
            var start = m().startOf('day');
            var end = m().endOf('day');
            this.selectRange(start, end);
        },

        selectYesterday() {
            var start = m()
                .subtract(1, 'day')
                .startOf('day');
            var end = m()
                .subtract(1, 'day')
                .endOf('day');
            this.selectRange(start, end);
        },

        selectThisWeek() {
            let start = m()
                .startOf('week')
                .startOf('day');

            let end = m()
                .endOf('week')
                .endOf('day');

            this.selectRange(start, end);
        },

        selectLastWeek() {
            let start = m()
                .subtract(1, 'week')
                .startOf('week')
                .startOf('day');

            let end = m()
                .subtract(1, 'week')
                .endOf('week')
                .endOf('day');

            this.selectRange(start, end);
        },

        selectThisMonth() {
            var start = m()
                .startOf('month')
                .startOf('day');
            var end = m()
                .endOf('month')
                .endOf('day');
            this.selectRange(start, end);
        },

        selectLastMonth() {
            var start = m()
                .subtract(1, 'month')
                .startOf('month')
                .startOf('day');
            var end = m()
                .subtract(1, 'month')
                .endOf('month')
                .endOf('day');
            this.selectRange(start, end);
        },

        selectMonth(date) {
            var start = m(this.currentDate)
                .startOf('month')
                .startOf('day');
            var end = m(this.currentDate)
                .endOf('month')
                .endOf('day');
            this.selectRange(start, end);
        },

        setStartAndEndDate() {
            if (this.start == null && this.end == null) {
                if (this.autoInitDate) {
                    this.selectToday();
                } else {
                    this.clearDate();
                }

                return;
            }

            if (this.start != null) {
                this.startDate = m(this.start, this.format);
                this.navigateToDate(this.startDate);
            }

            if (this.end != null) {
                this.endDate = m(this.end, this.format);
            }
        },

        extraPreviousWeekEnabled() {
            return m(this.currentDate)
                .startOf('month')
                .endOf('week')
                .subtract(4, 'day')
                .isSame(this.currentDate, 'month');
        },

        extraNextWeekEnabled() {
            return m(this.currentDate)
                .endOf('month')
                .startOf('week')
                .add(4, 'day')
                .isSame(this.currentDate, 'month');
        },

        getDateClass(date) {
            var clazz = [];

            if (!this.currentDate.isSame(date, 'month')) {
                clazz.push('datepicker-day-different-month');
            }

            if (m().isSame(date, 'day')) {
                clazz.push('datepicker-day-today');
            }

            if (
                date.isBetween(
                    this.getStartDate(),
                    this.getEndDate(),
                    'day',
                    '[]',
                )
            ) {
                clazz.push('datepicker-day-selected');
            }

            if (date.isSame(this.getStartDate(), 'day')) {
                clazz.push('datepicker-day-start');
            }

            if (date.isSame(this.getEndDate(), 'day')) {
                clazz.push('datepicker-day-end');
            }

            if (
                this.getMinimumDate().isAfter(date) ||
                this.getMaximumDate().isBefore(date)
            ) {
                clazz.push('datepicker-day-disabled');
            }

            return clazz.join(' ');
        },

        getStartDate() {
            if (this.startDate == null) {
                return m().startOf('day');
            }

            return this.startDate;
        },

        getEndDate() {
            if (this.endDate == null) {
                if (this.startDate != null) {
                    // Clone before endOf('day') — it mutates in place, and
                    // getStartDate() returns the live this.startDate reference
                    // (not a copy) whenever it's set, so calling this without
                    // cloning silently corrupted this.startDate itself.
                    return m(this.getStartDate()).endOf('day');
                } else {
                    return m().endOf('day');
                }
            }

            return this.endDate;
        },

        getMinimumDate() {
            if (this.min != null) {
                return m(this.min, this.format);
            }

            return m('2014-01-01');
        },

        getMaximumDate() {
            if (this.max != null) {
                return m(this.max, this.format);
            }

            return m().endOf('day');
        },

        rangeIsExactlyOneMonth() {
            let lastDay = m(this.getStartDate())
                .endOf('month')
                .date();
            let isFirstDay = m(this.getStartDate()).date() == 1;
            let isLastDay = m(this.getEndDate()).date() == lastDay;

            return isFirstDay && isLastDay;
        },

        previous() {
            if (this.canGoPrevious) {
                this.currentDate = m(this.currentDate).subtract(1, 'month');
            }
        },

        next() {
            if (this.canGoNext) {
                this.currentDate = m(this.currentDate).add(1, 'month');
            }
        },

        previousRange() {
            let start;
            let end;

            if (this.rangeIsExactlyOneMonth()) {
                start = this.getStartDate()
                    .subtract(1, 'month')
                    .startOf('month');

                end = this.getEndDate()
                    .subtract(1, 'month')
                    .endOf('month');
            } else {
                let range =
                    m
                        .range(this.getStartDate(), this.getEndDate())
                        .diff('days') + 1;

                start = m(this.getStartDate()).subtract(range, 'day');

                end = m(this.getEndDate()).subtract(range, 'day');
            }

            this.selectRange(start, end);
            this.broadcastChange();
        },

        nextRange() {
            if (this.showDatePicker) {
                let start;
                let end;

                if (this.rangeIsExactlyOneMonth()) {
                    start = m(this.getStartDate())
                        .add(1, 'month')
                        .startOf('month');
                    end = m(this.getEndDate())
                        .add(1, 'month')
                        .endOf('month');
                } else {
                    let range =
                        m
                            .range(this.getStartDate(), this.getEndDate())
                            .diff('days') + 1;
                    start = m(this.getStartDate()).add(range, 'day');
                    end = m(this.getEndDate()).add(range, 'day');
                }

                if (start.isAfter(m().endOf('day'))) return;

                this.selectRange(start, end);
                this.broadcastChange();
            }
        },

        noDateDefine() {
            return this.startDate == null && this.endDate == null;
        },

        broadcastChange(s) {
            let payload;
            let start;
            let end;

            start = this.startDate;
            end = this.endDate;

            payload = {
                start: start.format(),
                end: end.format(),
            };

            if (this.useVuex) {
                this.$store.commit('setReportRange', {
                    start,
                    end,
                });
            }

            this.$emit('onChange', payload);
        },

        openTimePicker(mode) {
            this.timeInputMode = mode;
            this.showTimePicker = true;

            if (mode === 'start') {
                this.selectedHour = this.startHour;
                this.selectedMinute = this.startMinute;
                this.selectedSecond = this.startSecond;
            }

            if (mode === 'end') {
                this.selectedHour = this.endHour;
                this.selectedMinute = this.endMinute;
                this.selectedSecond = this.endSecond;
            }
        },

        decreaseHour() {
            if (this.selectedHour > 0) {
                this.selectedHour--;
            } else {
                this.selectedHour = 23;
            }
        },

        decreaseMinute() {
            if (this.selectedMinute > 0) {
                this.selectedMinute--;
            } else {
                this.selectedMinute = 59;
            }
        },

        decreaseSecond() {
            if (this.selectedSecond > 0) {
                this.selectedSecond--;
            } else {
                this.selectedSecond = 59;
            }
        },

        increaseHour() {
            if (this.selectedHour < 23) {
                this.selectedHour++;
            } else {
                this.selectedHour = 0;
            }
        },

        increaseMinute() {
            if (this.selectedMinute < 59) {
                this.selectedMinute++;
            } else {
                this.selectedMinute = 0;
            }
        },

        increaseSecond() {
            if (this.selectedSecond < 59) {
                this.selectedSecond++;
            } else {
                this.selectedSecond = 0;
            }
        },

        changeMeridiem() {
            if (this.selectedMeridiem === 'AM') {
                this.selectedMeridiem = 'PM';
            } else {
                this.selectedMeridiem = 'AM';
            }
        },

        applyTime() {
            this.showTimePicker = false;

            let hour = this.selectedHour;

            if (this.timeInputMode === 'start') {
                let newtime = m(this.getStartDate())
                    .hour(hour)
                    .minute(this.selectedMinute)
                    .second(this.selectedSecond);

                this.setStartDate(newtime, false);
            }

            if (this.timeInputMode === 'end') {
                let newtime = m(this.getEndDate())
                    .hour(hour)
                    .minute(this.selectedMinute)
                    .second(this.selectedSecond);

                this.setEndDate(newtime, false);
            }
        },

        renderTimeNumber(n) {
            return leftPad(n, 2, '0');
        },

        openMinuteInput() {
            this.showApplyTimeButton = false;
            this.showMinuteInput = true;
            this.showHourInput = false;
            this.showSecondInput = false;
        },

        closeMinuteInput() {
            this.showApplyTimeButton = true;
            this.showMinuteInput = false;
        },

        openHourInput() {
            this.showApplyTimeButton = false;
            this.showHourInput = true;
            this.showMinuteInput = false;
            this.showSecondInput = false;
        },

        closeHourInput() {
            this.showApplyTimeButton = true;
            this.showHourInput = false;
        },

        openSecondInput() {
            this.showApplyTimeButton = false;
            this.showSecondInput = true;
            this.showHourInput = false;
            this.showMinuteInput = false;
        },

        closeSecondInput() {
            this.showApplyTimeButton = true;
            this.showSecondInput = false;
        },

        selectMinute(minute) {
            this.closeMinuteInput();
            this.selectedMinute = minute;
        },

        selectHour(hour) {
            this.closeHourInput();
            this.selectedHour = hour;
        },

        selectSecond(second) {
            this.closeSecondInput();
            this.selectedSecond = second;
        },

        validateAndApplyDateInputs() {
            const startValue = this.startDateDisplay;
            const endValue = this.endDateDisplay;

            if (!startValue || !endValue) {
                return;
            }

            const startMoment = m(startValue, 'DD-MM-YYYY', true);
            const endMoment = m(endValue, 'DD-MM-YYYY', true);

            if (!startMoment.isValid() || !endMoment.isValid()) {
                this.dateInputError = 'Invalid date format. Use DD-MM-YYYY';
                return;
            }

            const diffDays = endMoment.diff(startMoment, 'days');
            if (diffDays > 31) {
                this.dateInputError = 'Date range cannot exceed 31 days';
                return;
            }

            if (startMoment.isAfter(endMoment)) {
                this.dateInputError = 'Start date cannot be after end date';
                return;
            }

            this.dateInputError = '';
            this.selectRange(
                startMoment.startOf('day'),
                endMoment.endOf('day'),
            );
            this.clearDateInputs();
            this.broadcastChange();
        },

        clearDateInputs() {
            this.startDateInput = '';
            this.endDateInput = '';
        },

        onDateInputChange() {
            if (this.dateInputTimeout) {
                clearTimeout(this.dateInputTimeout);
            }

            this.dateInputError = '';

            this.dateInputTimeout = setTimeout(() => {
                this.validateAndApplyDateInputs();
            }, 500);
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
@import '../../sass/mixins';

$border-color: #ccc;

%body {
    display: inline-block;
    width: 28px;
    line-height: 28px;
    height: 28px;
    border-radius: 4px;
    text-align: center;
    @include user-select(none);
}

%body-hover {
    @extend %body;
    cursor: pointer;

    &:hover {
        background-color: #eeeeee;
    }
}

%top-divider {
    border-top: 1px solid $border-color;
    margin-top: 10px;
    padding-top: 10px;
}

.datepicker {
    position: relative;
}

.datepicker-inner {
    display: flex;
    width: 360px;

    > div {
        float: left;
    }
}

.datepicker-body {
    width: 218px;
    padding: 10px 10px;
    border-right: 1px solid $border-color;
    display: inline-block;
}

.datepicker-shortcuts {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 10px 10px;
    width: 142px;
    vertical-align: top;
}

.datepicker-dateRange {
    span {
        @extend %body-hover;
    }
}

.datepicker-weekRange {
    span {
        @extend %body;
        font-size: 10px;
        font-weight: bold;
        text-transform: uppercase;
    }
}

.datepicker-day-disabled {
    color: #ddd !important;
    cursor: not-allowed !important;

    &:hover {
        background-color: transparent !important;
    }
}

.datepicker-day-different-month {
    color: #999;
}

.datepicker-day-today {
    font-weight: bold;
}

.datepicker-day-selected {
    background-color: #0cc2aa;
    border-radius: 0 !important;
    color: #fff;

    &:hover {
        background-color: #0cc2aa !important;
    }
}

.datepicker-day-start {
    border-bottom-left-radius: 4px !important;
    border-top-left-radius: 4px !important;
}

.datepicker-day-end {
    border-bottom-right-radius: 4px !important;
    border-top-right-radius: 4px !important;
}

.datepicker-ctrl {
    position: relative;
    height: 30px;
    line-height: 30px;
    font-weight: bold;
    text-align: center;

    p {
        @extend %body-hover;
        width: 65%;
    }

    span {
        @extend %body;
        position: absolute;
        cursor: pointer;
    }
}

.datepicker-popup {
    position: absolute;
    right: 0;
    border: 1px solid $border-color;
    border-radius: 5px;
    background: #fff;
    margin-top: 2px;
    z-index: 1000;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
}

.datepicker-timeInput {
    div {
        margin-bottom: 0.5rem;

        &:last-child {
            margin-bottom: 0;
        }
    }

    p {
        font-size: 10px;
        font-weight: bold;
        margin-bottom: 0;
        text-transform: uppercase;
    }

    span.clickable {
        @extend %body-hover;
    }

    .colon {
        width: auto;
    }
}

.datepicker-timeInput:not(.has-date-range-info) {
    @extend %top-divider;
}

.datepicker-minuteInput,
.datepicker-hourInput {
    margin-bottom: 1px;

    span {
        @extend %body-hover;
        height: 31px;
        width: 49px;
    }
}

.datepicker-preBtn,
.datepicker-nextBtn {
    &:hover {
        background-color: #eeeeee;
    }
}

.datepicker-preBtn {
    left: 2px;
}

.datepicker-nextBtn {
    right: 2px;
}

.shortcut-selector {
    @extend %top-divider;
    text-align: center;
}

.summary-group {
    width: 100%;
    display: flex;

    .chevron {
        display: inline-block;
        width: 0;
        height: 0;
        margin-right: 0.25rem;
        margin-left: 0.25rem;
        vertical-align: middle;
        content: '';
    }

    .prev {
        float: left;
        margin-right: -1px;

        &::after {
            @extend .chevron;
            border-top: 0.3em solid transparent;
            border-right: 0.3em solid;
            border-bottom: 0.3em solid transparent;
        }
    }

    .summary {
        overflow: hidden;
        flex-grow: 1;
        margin-left: -1px;
        margin-right: -1px;

        .btn {
            width: 100%;
            -webkit-border-radius: 0;
            -moz-border-radius: 0;
            border-radius: 0;
        }

        .popup-open {
            &::after {
                @extend .chevron;
                border-bottom: 0.3em solid;
                border-left: 0.3em solid transparent;
                border-right: 0.3em solid transparent;
            }
        }

        .popup-close {
            &::after {
                @extend .chevron;
                border-left: 0.3em solid transparent;
                border-right: 0.3em solid transparent;
                border-top: 0.3em solid;
            }
        }
    }

    .rounded-btn {
        .btn {
            border-radius: 4px;
        }
    }

    .next {
        float: right;

        &::after {
            @extend .chevron;
            border-top: 0.3em solid transparent;
            border-left: 0.3em solid;
            border-bottom: 0.3em solid transparent;
        }
        &:disabled {
            cursor: not-allowed !important;
            opacity: 0.4;
            pointer-events: auto !important;
        }
    }
}

.timepicker-buttons {
    @extend %top-divider;
}

.timepicker-fc {
    min-width: 30%;
}

.timepicker-row {
    display: table-row;

    div {
        display: table-cell;
        padding: 4px;
    }
}

.timepicker-table {
    display: table;
    margin-bottom: 10px;
    text-align: center;
    width: 100%;
}

.filter-mode {
    border-bottom: 1px solid #ccc;
    display: flex;
    padding: 10px;
    width: 100%;

    .btn {
        width: 100%;
    }
}

.date-input {
    flex: 1;
    min-width: 85px;
    width: auto;
    font-size: 14px;
    padding: 4px 8px;
}

.date-range-info {
    @extend %top-divider;

    margin-bottom: 0.5rem;

    p {
        font-size: 10px;
        font-weight: bold;
        margin-bottom: 0;
        text-transform: uppercase;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/Datepicker.vue